import { Layout } from "antd";
import { Footer } from "antd/es/layout/layout";
import type { ReactNode } from "react";
import { Outlet } from "react-router";
import {
	Sidebar,
	SidebarMenu,
	type SidebarProps,
	Topbar,
	type TopbarProps,
} from ".";

export interface ThemeColors {
	primary?: string;
	primaryLight?: string;
	secondary?: string;
}

export interface PageLayoutComponentProps {
	children?: ReactNode;
	sideBarProps: SidebarProps;
	topBarProps: TopbarProps;
	themeColors?: ThemeColors;
}
const { Header, Sider, Content } = Layout;

export function PageLayoutComponent(props: PageLayoutComponentProps) {
	const { children, sideBarProps, topBarProps, themeColors } = props;

	return (
		<Layout className="min-h-screen">
			<Header
				className="sticky top-0 z-[11] w-full flex items-center h-[52px] border-x-0 border-t-0 border-b-2 border-solid p-0 md:h-16"
				style={{
					backgroundColor: "#ffffff",
					borderBottomColor: themeColors?.primary || "#e5e7eb",
				}}
			>
				<Topbar {...topBarProps} themeColors={themeColors} />
			</Header>
			<Layout className="flex flex-row">
				{/* Sidebar: Hidden by default (on small screens), appears at 'md' breakpoint */}
				<Sider
					className="hidden overflow-auto h-screen fixed left-0 pt-16 top-0 bottom-0 md:block"
					style={{
						backgroundColor: themeColors?.primaryLight || "#ffffff",
					}}
				>
					<Sidebar {...sideBarProps} themeColors={themeColors} />
				</Sider>
				<Content
					// Content:
					// On small screens: full width, no left margin/padding for sidebar
					// On 'md' screens and up: takes full width MINUS sidebar width,
					// by using a left margin/padding equivalent to the sidebar's width.
					// The 'lg:pl-[12.5rem]' seems to imply a sidebar width of 12.5rem.
					// Let's use `md:ml-[12.5rem]` or similar if the sidebar is fixed width.
					// If the sidebar is fixed width (e.g., 200px or ~12.5rem), then `md:ml-[12.5rem]` or `md:pl-[12.5rem]`
					// on the content will align it perfectly with the sidebar's presence.
					// I'll assume 12.5rem for the sidebar width based on your previous `lg:pl-[12.5rem]`.
					className="w-full grow bg-white p-0 md:p-4 md:pr-0 md:ml-[12.5rem]" // Apply ml-200px equivalent here
				>
					{children}
					<Outlet />
				</Content>
			</Layout>
			{/* Footer: Visible by default (on small screens), hidden at 'md' breakpoint */}
			<Footer
				className="sticky bottom-0 z-10 text-center p-0 border-t h-16 md:hidden"
				style={{
					backgroundColor: themeColors?.primaryLight || "#f3f4f6",
					borderTopColor: themeColors?.primary || "#d1d5db",
				}}
			>
				<SidebarMenu
					mode="horizontal"
					{...sideBarProps}
					themeColors={themeColors}
				/>
			</Footer>
		</Layout>
	);
}
